/**
 * Lattice Playground
 * Visualizes the hard problems behind lattice-based post-quantum cryptography:
 * basis reduction + the Shortest Vector Problem, the Closest Vector Problem,
 * and GGH-style encryption that turns CVP into a one-way trapdoor.
 */
import {
  LATTICE_PRESETS,
  badBasis,
  generateLatticePoints,
  gaussReduce,
  shortestVector,
  orthogonalityDefect,
  gramSchmidtLengths,
  babaiRound,
  closestVector,
  gghEncrypt,
  gghDecrypt,
  add,
  sub,
  norm,
  type Basis,
  type Vec2,
  type LatticePreset,
  type ReductionStep,
} from '../../lib/labs/lattice';

// ===== DOM =====
const canvas = document.getElementById('lattice-canvas') as HTMLCanvasElement;
const latticeSelector = document.getElementById('lattice-selector') as HTMLElement;
const modeSelector = document.getElementById('mode-selector') as HTMLElement;
const readout = document.getElementById('lattice-readout') as HTMLElement;
const hint = document.getElementById('lattice-hint') as HTMLElement;

const basesControls = document.getElementById('controls-bases') as HTMLElement;
const cvpControls = document.getElementById('controls-cvp') as HTMLElement;
const gghControls = document.getElementById('controls-ggh') as HTMLElement;

const reduceBtn = document.getElementById('reduce-btn') as HTMLButtonElement;
const basisGoodBtn = document.getElementById('basis-good') as HTMLButtonElement;
const basisBadBtn = document.getElementById('basis-bad') as HTMLButtonElement;
const toggleCell = document.getElementById('toggle-cell') as HTMLInputElement;
const toggleSv = document.getElementById('toggle-sv') as HTMLInputElement;

const cvpGoodBtn = document.getElementById('cvp-good') as HTMLButtonElement;
const cvpBadBtn = document.getElementById('cvp-bad') as HTMLButtonElement;
const toggleVoronoi = document.getElementById('toggle-voronoi') as HTMLInputElement;

const gghM1 = document.getElementById('ggh-m1') as HTMLInputElement;
const gghM2 = document.getElementById('ggh-m2') as HTMLInputElement;
const gghM1Val = document.getElementById('ggh-m1-val') as HTMLElement;
const gghM2Val = document.getElementById('ggh-m2-val') as HTMLElement;
const gghNewError = document.getElementById('ggh-new-error') as HTMLButtonElement;
const gghDecryptGood = document.getElementById('ggh-decrypt-good') as HTMLButtonElement;
const gghDecryptBad = document.getElementById('ggh-decrypt-bad') as HTMLButtonElement;

// ===== types =====
type Mode = 'bases' | 'cvp' | 'ggh';

// ===== state =====
let preset: LatticePreset = LATTICE_PRESETS[0];
let mode: Mode = 'bases';

// The basis currently drawn / dragged. In bases mode this can be edited freely.
let basis: Basis = preset.good;
let usingGoodBasis = true;
let showCell = true;
let showShortest = true;

// CVP
let target: Vec2 = [1.4, 0.9];
let cvpGood = true;
let showVoronoi = false;

// GGH
let message: [number, number] = [2, -1];
let error: Vec2 = [0, 0];
let gghDecryptWithGood = true;

// reduction animation
let reduceSteps: ReductionStep[] = [];
let reduceIndex = 0;
let reduceTimer: number | null = null;

// dragging
let dragHandle: 'b1' | 'b2' | 'target' | null = null;

// view
const VIEW_HALF = 6; // world units from origin to the nearest edge

// ===== colors =====
const C = {
  bg: '#0d1117',
  grid: 'rgba(148,163,184,0.10)',
  axis: 'rgba(148,163,184,0.45)',
  dot: '#64748b',
  dotGlow: 'rgba(96,165,250,0.25)',
  b1: '#60a5fa',
  b2: '#f472b6',
  cell: 'rgba(96,165,250,0.10)',
  shortest: '#fbbf24',
  target: '#f87171',
  closest: '#34d399',
  babaiGood: '#34d399',
  babaiBad: '#fb923c',
  cipher: '#f87171',
  point: '#a78bfa',
  ok: '#34d399',
  bad: '#fb923c',
};

// ===== transforms (isotropic, origin centered) =====
function dpr(): number {
  return window.devicePixelRatio || 1;
}

function plot() {
  const pad = 14 * dpr();
  return {
    pad,
    left: pad,
    top: pad,
    w: canvas.width - 2 * pad,
    h: canvas.height - 2 * pad,
  };
}

function scalePx(): number {
  const p = plot();
  return Math.min(p.w, p.h) / (2 * VIEW_HALF);
}

function originPx(): [number, number] {
  const p = plot();
  return [p.left + p.w / 2, p.top + p.h / 2];
}

function worldToCanvas(x: number, y: number): [number, number] {
  const s = scalePx();
  const [ox, oy] = originPx();
  return [ox + x * s, oy - y * s];
}

function canvasToWorld(cssX: number, cssY: number): Vec2 {
  const s = scalePx();
  const [ox, oy] = originPx();
  const x = (cssX * dpr() - ox) / s;
  const y = (oy - cssY * dpr()) / s;
  return [x, y];
}

function worldBounds() {
  const p = plot();
  const s = scalePx();
  const halfW = p.w / 2 / s;
  const halfH = p.h / 2 / s;
  return { xMin: -halfW, xMax: halfW, yMin: -halfH, yMax: halfH };
}

// ===== canvas sizing =====
function sizeCanvas(): void {
  const rect = canvas.getBoundingClientRect();
  const d = dpr();
  canvas.width = rect.width * d;
  canvas.height = rect.height * d;
}

// ===== drawing helpers =====
function drawArrow(
  ctx: CanvasRenderingContext2D,
  from: Vec2,
  to: Vec2,
  color: string,
  width: number
): void {
  const [fx, fy] = worldToCanvas(from[0], from[1]);
  const [tx, ty] = worldToCanvas(to[0], to[1]);
  const d = dpr();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width * d;
  ctx.beginPath();
  ctx.moveTo(fx, fy);
  ctx.lineTo(tx, ty);
  ctx.stroke();

  const angle = Math.atan2(ty - fy, tx - fx);
  const head = 9 * d;
  ctx.beginPath();
  ctx.moveTo(tx, ty);
  ctx.lineTo(tx - head * Math.cos(angle - 0.4), ty - head * Math.sin(angle - 0.4));
  ctx.lineTo(tx - head * Math.cos(angle + 0.4), ty - head * Math.sin(angle + 0.4));
  ctx.closePath();
  ctx.fill();
}

function drawDot(
  ctx: CanvasRenderingContext2D,
  p: Vec2,
  r: number,
  color: string,
  glow = false
): void {
  const [cx, cy] = worldToCanvas(p[0], p[1]);
  const d = dpr();
  if (glow) {
    ctx.fillStyle = C.dotGlow;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 2.4 * d, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, r * d, 0, Math.PI * 2);
  ctx.fill();
}

function drawDashed(
  ctx: CanvasRenderingContext2D,
  from: Vec2,
  to: Vec2,
  color: string
): void {
  const [fx, fy] = worldToCanvas(from[0], from[1]);
  const [tx, ty] = worldToCanvas(to[0], to[1]);
  const d = dpr();
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5 * d;
  ctx.setLineDash([5 * d, 4 * d]);
  ctx.beginPath();
  ctx.moveTo(fx, fy);
  ctx.lineTo(tx, ty);
  ctx.stroke();
  ctx.restore();
}

// ===== main draw =====
function draw(): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  drawAxes(ctx);
  drawLatticeDots(ctx);

  if (mode === 'bases') drawBasesMode(ctx);
  else if (mode === 'cvp') drawCvpMode(ctx);
  else drawGghMode(ctx);
}

function drawAxes(ctx: CanvasRenderingContext2D): void {
  const b = worldBounds();
  const d = dpr();
  // faint integer-ish grid
  ctx.strokeStyle = C.grid;
  ctx.lineWidth = d;
  for (let gx = Math.ceil(b.xMin); gx <= b.xMax; gx++) {
    const [px] = worldToCanvas(gx, 0);
    const [, top] = worldToCanvas(0, b.yMax);
    const [, bot] = worldToCanvas(0, b.yMin);
    ctx.beginPath();
    ctx.moveTo(px, top);
    ctx.lineTo(px, bot);
    ctx.stroke();
  }
  for (let gy = Math.ceil(b.yMin); gy <= b.yMax; gy++) {
    const [, py] = worldToCanvas(0, gy);
    const [left] = worldToCanvas(b.xMin, 0);
    const [right] = worldToCanvas(b.xMax, 0);
    ctx.beginPath();
    ctx.moveTo(left, py);
    ctx.lineTo(right, py);
    ctx.stroke();
  }
  // axes
  ctx.strokeStyle = C.axis;
  ctx.lineWidth = 1.4 * d;
  const [ox, oy] = originPx();
  ctx.beginPath();
  ctx.moveTo(plot().left, oy);
  ctx.lineTo(plot().left + plot().w, oy);
  ctx.moveTo(ox, plot().top);
  ctx.lineTo(ox, plot().top + plot().h);
  ctx.stroke();
}

function drawLatticeDots(ctx: CanvasRenderingContext2D): void {
  const pts = generateLatticePoints(basis, worldBounds());
  for (const p of pts) {
    const isOrigin = p.a === 0 && p.b === 0;
    drawDot(
      ctx,
      [p.x, p.y],
      isOrigin ? 3.5 : 2.6,
      isOrigin ? '#e2e8f0' : C.dot,
      isOrigin
    );
  }
}

function drawFundamentalCell(ctx: CanvasRenderingContext2D): void {
  const [b1, b2] = basis;
  const pts: Vec2[] = [[0, 0], b1, add(b1, b2), b2];
  ctx.fillStyle = C.cell;
  ctx.beginPath();
  for (let i = 0; i < pts.length; i++) {
    const [cx, cy] = worldToCanvas(pts[i][0], pts[i][1]);
    if (i === 0) ctx.moveTo(cx, cy);
    else ctx.lineTo(cx, cy);
  }
  ctx.closePath();
  ctx.fill();
}

function drawBasesMode(ctx: CanvasRenderingContext2D): void {
  if (showCell) drawFundamentalCell(ctx);

  const [b1, b2] = basis;
  drawArrow(ctx, [0, 0], b1, C.b1, 2.5);
  drawArrow(ctx, [0, 0], b2, C.b2, 2.5);
  // draggable handles
  drawDot(ctx, b1, 5, C.b1);
  drawDot(ctx, b2, 5, C.b2);

  if (showShortest) {
    const sv = shortestVector(basis);
    drawArrow(ctx, [0, 0], sv, C.shortest, 3);
    drawDot(ctx, sv, 4, C.shortest, true);
  }

  const defect = orthogonalityDefect(basis);
  const sv = shortestVector(basis);
  setReadout([
    ['Basis vectors', `|b1| = ${norm(b1).toFixed(2)},  |b2| = ${norm(b2).toFixed(2)}`],
    ['Orthogonality defect', `${defect.toFixed(2)} (1.00 is perfectly orthogonal)`],
    ['Shortest vector', `length ${norm(sv).toFixed(3)}`],
  ]);
}

function drawCvpMode(ctx: CanvasRenderingContext2D): void {
  const useBasis = cvpGood ? preset.good : badBasis(preset);
  // ensure we draw the lattice of the active preset (not a freely-edited basis)
  const truth = closestVector(preset.good, target);
  const babai = babaiRound(useBasis, target);

  if (showVoronoi) drawVoronoiCell(ctx, truth.point);

  // rounding construction lines for the chosen basis
  drawDashed(ctx, target, babai.point, cvpGood ? C.babaiGood : C.babaiBad);

  // true closest point
  drawDot(ctx, truth.point, 6, C.closest, true);
  // babai guess
  drawDot(ctx, babai.point, 6, cvpGood ? C.babaiGood : C.babaiBad);
  // target
  drawDot(ctx, target, 6, C.target, true);

  const correct = norm(sub(babai.point, truth.point)) < 1e-6;
  setReadout([
    ['Decoding with', cvpGood ? 'GOOD basis (private key)' : 'BAD basis (public key)'],
    [
      'Babai guess',
      `(${babai.coeffs[0]}, ${babai.coeffs[1]}) -> distance ${norm(sub(babai.point, target)).toFixed(3)}`,
    ],
    ['True closest', `distance ${truth.dist.toFixed(3)}`],
    [
      'Result',
      correct
        ? 'Correct: rounding found the closest point'
        : 'WRONG: rounding missed by ' + norm(sub(babai.point, truth.point)).toFixed(2),
    ],
  ]);
}

function drawVoronoiCell(ctx: CanvasRenderingContext2D, center: Vec2): void {
  // Approximate the Voronoi cell by sampling: shade pixels closest to `center`.
  // Cheap and clear enough at this resolution.
  const useBasis = preset.good;
  const step = 6 * dpr();
  ctx.save();
  for (let py = plot().top; py < plot().top + plot().h; py += step) {
    for (let px = plot().left; px < plot().left + plot().w; px += step) {
      const wx = (px - originPx()[0]) / scalePx();
      const wy = (originPx()[1] - py) / scalePx();
      const near = closestVector(useBasis, [wx, wy]).point;
      if (norm(sub(near, center)) < 1e-6) {
        ctx.fillStyle = 'rgba(52,211,153,0.10)';
        ctx.fillRect(px, py, step, step);
      }
    }
  }
  ctx.restore();
}

function drawGghMode(ctx: CanvasRenderingContext2D): void {
  const pub = badBasis(preset);
  const { point, ciphertext } = gghEncrypt(pub, message, error);
  const dec = gghDecrypt(gghDecryptWithGood ? preset.good : pub, pub, ciphertext);

  // message lattice point (the secret we want back)
  drawDot(ctx, point, 6, C.point, true);
  // error vector from message point to ciphertext
  drawArrow(ctx, point, ciphertext, 'rgba(248,113,113,0.7)', 2);
  // ciphertext
  drawDot(ctx, ciphertext, 6, C.cipher, true);
  // recovered point
  drawDashed(ctx, ciphertext, dec.point, gghDecryptWithGood ? C.ok : C.bad);
  drawDot(ctx, dec.point, 7, gghDecryptWithGood ? C.ok : C.bad);

  const ok = dec.message[0] === message[0] && dec.message[1] === message[1];
  setReadout([
    ['Message', `(${message[0]}, ${message[1]})`],
    ['Ciphertext', 'message point + small error vector'],
    [
      'Decrypting with',
      gghDecryptWithGood ? 'GOOD basis (private key)' : 'BAD basis (attacker)',
    ],
    ['Recovered', `(${dec.message[0]}, ${dec.message[1]})`],
    [
      'Result',
      ok ? 'Decryption succeeded' : 'FAILED: attacker recovered the wrong message',
    ],
  ]);
}

// ===== readout =====
function setReadout(rows: [string, string][]): void {
  readout.innerHTML = rows
    .map(
      ([k, v]) =>
        `<div class="flex flex-col gap-0.5 rounded-lg border border-border/70 bg-surface/50 p-3">
          <span class="text-xs uppercase tracking-[0.2em] text-muted">${k}</span>
          <span class="font-mono text-sm text-text">${v}</span>
        </div>`
    )
    .join('');
}

// ===== GGH error generation =====
function newGghError(): void {
  // Keep the error inside the good basis's correctable radius so the private
  // key always wins, while the public key may or may not.
  const [g1, g2] = gramSchmidtLengths(preset.good);
  const radius = Math.min(g1, g2) * 0.45;
  const angle = Math.random() * Math.PI * 2;
  const r = radius * (0.4 + 0.6 * Math.random());
  error = [r * Math.cos(angle), r * Math.sin(angle)];
}

// ===== reduction animation =====
function startReduction(): void {
  stopReduction();
  reduceSteps = gaussReduce(basis).steps;
  reduceIndex = 0;
  if (reduceSteps.length <= 1) return;
  reduceTimer = window.setInterval(() => {
    reduceIndex++;
    if (reduceIndex >= reduceSteps.length) {
      basis = reduceSteps[reduceSteps.length - 1].basis;
      usingGoodBasis = false; // it's a reduced basis, not necessarily the stored "good" preset
      stopReduction();
      draw();
      return;
    }
    basis = reduceSteps[reduceIndex].basis;
    hint.textContent = reduceSteps[reduceIndex].note;
    draw();
  }, 650);
}

function stopReduction(): void {
  if (reduceTimer != null) {
    clearInterval(reduceTimer);
    reduceTimer = null;
  }
}

// ===== mode + preset wiring =====
function loadPreset(p: LatticePreset): void {
  preset = p;
  basis = usingGoodBasis ? p.good : badBasis(p);
  newGghError();
  for (const btn of latticeSelector.querySelectorAll<HTMLButtonElement>('.preset-btn')) {
    btn.classList.toggle('active', btn.dataset.preset === p.id);
  }
  draw();
}

function setMode(next: Mode): void {
  mode = next;
  stopReduction();
  for (const btn of modeSelector.querySelectorAll<HTMLButtonElement>('.mode-btn')) {
    btn.classList.toggle('active', btn.dataset.mode === next);
  }
  basesControls.style.display = next === 'bases' ? '' : 'none';
  cvpControls.style.display = next === 'cvp' ? '' : 'none';
  gghControls.style.display = next === 'ggh' ? '' : 'none';

  const hints: Record<Mode, string> = {
    bases:
      'Drag the blue and pink arrowheads to reshape the lattice. Same dots, many bases.',
    cvp: 'Drag the red target point. The good basis rounds to the closest dot; the bad basis can miss.',
    ggh: 'A message becomes a lattice point plus a small error. Only the private key decodes it reliably.',
  };
  hint.textContent = hints[next];
  // reset basis to preset when leaving bases editing
  if (next !== 'bases') basis = usingGoodBasis ? preset.good : badBasis(preset);
  canvas.style.cursor = next === 'ggh' ? 'default' : 'crosshair';
  draw();
}

function setBasisGood(good: boolean): void {
  usingGoodBasis = good;
  basis = good ? preset.good : badBasis(preset);
  basisGoodBtn.classList.toggle('active', good);
  basisBadBtn.classList.toggle('active', !good);
  hint.textContent = good
    ? 'Good basis: short and nearly orthogonal. This is the private key.'
    : 'Bad basis: long and skewed. Same lattice, but it is the public key. Hit Reduce.';
  draw();
}

// ===== dragging =====
function handleAt(cssX: number, cssY: number): 'b1' | 'b2' | 'target' | null {
  const d = dpr();
  const thresh = 16 * d;
  const px = cssX * d;
  const py = cssY * d;
  const near = (p: Vec2) => {
    const [hx, hy] = worldToCanvas(p[0], p[1]);
    return Math.hypot(hx - px, hy - py) < thresh;
  };
  if (mode === 'bases') {
    if (near(basis[0])) return 'b1';
    if (near(basis[1])) return 'b2';
  }
  if (mode === 'cvp') {
    if (near(target)) return 'target';
  }
  return null;
}

function bindEvents(): void {
  canvas.addEventListener('mousedown', (e) => {
    const rect = canvas.getBoundingClientRect();
    const h = handleAt(e.clientX - rect.left, e.clientY - rect.top);
    if (h) {
      dragHandle = h;
    } else if (mode === 'cvp') {
      // click anywhere in CVP to move the target
      target = canvasToWorld(e.clientX - rect.left, e.clientY - rect.top);
      dragHandle = 'target';
      draw();
    }
  });

  window.addEventListener('mousemove', (e) => {
    if (!dragHandle) return;
    const rect = canvas.getBoundingClientRect();
    const w = canvasToWorld(e.clientX - rect.left, e.clientY - rect.top);
    if (dragHandle === 'b1') {
      basis = [w, basis[1]];
      usingGoodBasis = false;
    } else if (dragHandle === 'b2') {
      basis = [basis[0], w];
      usingGoodBasis = false;
    } else if (dragHandle === 'target') {
      target = w;
    }
    draw();
  });

  window.addEventListener('mouseup', () => {
    dragHandle = null;
  });

  // touch support for tablets / phones
  canvas.addEventListener(
    'touchstart',
    (e) => {
      const t = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      const h = handleAt(t.clientX - rect.left, t.clientY - rect.top);
      if (h) {
        dragHandle = h;
        e.preventDefault();
      } else if (mode === 'cvp') {
        target = canvasToWorld(t.clientX - rect.left, t.clientY - rect.top);
        dragHandle = 'target';
        e.preventDefault();
        draw();
      }
    },
    { passive: false }
  );
  canvas.addEventListener(
    'touchmove',
    (e) => {
      if (!dragHandle) return;
      const t = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      const w = canvasToWorld(t.clientX - rect.left, t.clientY - rect.top);
      if (dragHandle === 'b1') basis = [w, basis[1]];
      else if (dragHandle === 'b2') basis = [basis[0], w];
      else if (dragHandle === 'target') target = w;
      e.preventDefault();
      draw();
    },
    { passive: false }
  );
  canvas.addEventListener('touchend', () => {
    dragHandle = null;
  });

  // bases controls
  reduceBtn.addEventListener('click', startReduction);
  basisGoodBtn.addEventListener('click', () => setBasisGood(true));
  basisBadBtn.addEventListener('click', () => setBasisGood(false));
  toggleCell.addEventListener('change', () => {
    showCell = toggleCell.checked;
    draw();
  });
  toggleSv.addEventListener('change', () => {
    showShortest = toggleSv.checked;
    draw();
  });

  // cvp controls
  cvpGoodBtn.addEventListener('click', () => {
    cvpGood = true;
    cvpGoodBtn.classList.add('active');
    cvpBadBtn.classList.remove('active');
    draw();
  });
  cvpBadBtn.addEventListener('click', () => {
    cvpGood = false;
    cvpBadBtn.classList.add('active');
    cvpGoodBtn.classList.remove('active');
    draw();
  });
  toggleVoronoi.addEventListener('change', () => {
    showVoronoi = toggleVoronoi.checked;
    draw();
  });

  // ggh controls
  gghM1.addEventListener('input', () => {
    message = [parseInt(gghM1.value, 10), message[1]];
    gghM1Val.textContent = gghM1.value;
    draw();
  });
  gghM2.addEventListener('input', () => {
    message = [message[0], parseInt(gghM2.value, 10)];
    gghM2Val.textContent = gghM2.value;
    draw();
  });
  gghNewError.addEventListener('click', () => {
    newGghError();
    draw();
  });
  gghDecryptGood.addEventListener('click', () => {
    gghDecryptWithGood = true;
    gghDecryptGood.classList.add('active');
    gghDecryptBad.classList.remove('active');
    draw();
  });
  gghDecryptBad.addEventListener('click', () => {
    gghDecryptWithGood = false;
    gghDecryptBad.classList.add('active');
    gghDecryptGood.classList.remove('active');
    draw();
  });
}

function buildSelectors(): void {
  latticeSelector.innerHTML = '';
  for (const p of LATTICE_PRESETS) {
    const btn = document.createElement('button');
    btn.dataset.preset = p.id;
    btn.className =
      'preset-btn rounded-full border border-border/70 px-4 py-2 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = p.name;
    btn.title = p.description;
    btn.addEventListener('click', () => loadPreset(p));
    latticeSelector.appendChild(btn);
  }

  const modes: [Mode, string][] = [
    ['bases', 'Bases & SVP'],
    ['cvp', 'Closest Vector'],
    ['ggh', 'Encryption (GGH)'],
  ];
  modeSelector.innerHTML = '';
  for (const [id, label] of modes) {
    const btn = document.createElement('button');
    btn.dataset.mode = id;
    btn.className =
      'mode-btn rounded-full border border-border/70 px-4 py-2 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = label;
    btn.addEventListener('click', () => setMode(id));
    modeSelector.appendChild(btn);
  }
}

function setupSizing(): void {
  const observer = new ResizeObserver(() => {
    sizeCanvas();
    draw();
  });
  sizeCanvas();
  observer.observe(canvas.parentElement!);
}

function init(): void {
  buildSelectors();
  bindEvents();
  setupSizing();
  loadPreset(LATTICE_PRESETS[0]);
  setBasisGood(true);
  setMode('bases');
  gghM1Val.textContent = gghM1.value;
  gghM2Val.textContent = gghM2.value;
  message = [parseInt(gghM1.value, 10), parseInt(gghM2.value, 10)];
  newGghError();
  draw();
}

init();
